import React, { useState, useCallback, useMemo } from 'react';
import {
    Modal, Button, Icon, Message, Loader, Divider, Label,
    Form, Grid, Segment, Header, Progress
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../app/stores/store';
import {
    CalendarEventDTO, UpdateRecurringSeriesDTO,
    RecurringSkippedSlotDTO, DeleteRecurringResult, EditRecurringResult
} from '../../app/models/Calendar';
import PaymentStatusSelector from './PaymentStatusSelector';
import { CalendarStatusOption } from './SportAssetCalendar';

type TFunc = ReturnType<typeof useTranslation>['t'];

// ─── Types ────────────────────────────────────────────────────────────────────

interface RecurringActionDialogProps {
    open:            boolean;
    onClose:         () => void;
    event:           CalendarEventDTO | null;
    intent:          'edit' | 'delete';
    statusOptions:   CalendarStatusOption[];
    /**
     * BUG #3 FIX: Called when user chooses "Edit this occurrence only".
     * The dialog NO LONGER calls detachEventFromSeries here.
     * The parent must record that a detach is pending and fire the
     * actual API call only when the user saves their edits.
     */
    onThisOnly:      (event: CalendarEventDTO) => void;
    onSeriesDeleted: (result: DeleteRecurringResult) => void;
}

type DeleteMode = 'thisOnly' | 'thisAndFuture' | 'allFuture';

type DialogView =
    | 'choose'
    | 'editAllForm'
    | 'editAllResult'
    | 'deleteConfirm'
    | 'deleteResult';

interface EditFormState {
    newSlotStartTime:       string;
    newSlotEndTime:         string;
    changeTime:             boolean;
    capacityUnitsRequested: number;
    changeCapacity:         boolean;
    customerFullName:       string;
    customerPhone:          string;
    customerEmail:          string;
    note:                   string;
    isPaid:                 number;
    statusId:               string;
    // BUG #1 FIX: manual price override for "edit all in series"
    manualPriceOverride:    number | null;
    changeManualPrice:      boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build a full ISO-8601 UTC string from an existing slot Date and a local "HH:mm"
 * string from <input type="time">. Same approach as individual slots: send a real
 * Date.toISOString() so ASP.NET parses it as Kind=Utc with no extra logic needed.
 *
 * Example – user in UTC+1, existing slot on 2025-06-06, types "15:00":
 *   base = new Date("2025-06-06T13:00:00Z"), setHours(15,0) -> 15:00 local = 14:00 UTC
 *   .toISOString() = "2025-06-06T14:00:00.000Z"  ✓
 */
function localHHmmToIso(baseDate: Date, hhmm: string): string {
    const [h, m] = hhmm.split(':').map(Number);
    const d = new Date(baseDate);
    d.setHours(h, m, 0, 0);
    return d.toISOString();
}

/** Extract local HH:mm from a Date for pre-populating <input type="time"> */
function hhmmFromDate(d: Date): string {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const inputStyle: React.CSSProperties = {
    width: '100%', padding: '0.6rem',
    border: '1.5px solid #ced4da', borderRadius: '6px', fontSize: '0.93rem',
};
const labelStyle: React.CSSProperties = {
    fontWeight: 'bold', fontSize: '0.9rem',
    marginBottom: '0.35rem', display: 'block', color: '#34495e',
};

// ─── Component ────────────────────────────────────────────────────────────────

const RecurringActionDialog: React.FC<RecurringActionDialogProps> = observer(({
    open, onClose, event, intent, statusOptions, onThisOnly, onSeriesDeleted,
}) => {
    const { t } = useTranslation();
    const { calendarStore } = useStore();

    const [view, setView]                 = useState<DialogView>('choose');
    const [deleteMode, setDeleteMode]     = useState<DeleteMode>('thisAndFuture');
    const [working, setWorking]           = useState(false);
    const [error, setError]               = useState<string | null>(null);
    const [editResult, setEditResult]     = useState<EditRecurringResult | null>(null);
    const [deleteResult, setDeleteResult] = useState<DeleteRecurringResult | null>(null);
    // Validation: show field errors only after the user clicks Save
    const [showEditErrors, setShowEditErrors] = useState(false);

    const totalCapacity = useMemo(() => {
        if (!event) return 0;
        const res = calendarStore.resources.find(r => r.id === event.resourceId);
        return res?.totalCapacityUnits ?? 0;
    }, [event, calendarStore.resources]);

    const defaultForm = useMemo((): EditFormState => ({
        newSlotStartTime:       event ? hhmmFromDate(new Date(event.start)) : '10:00',
        newSlotEndTime:         event ? hhmmFromDate(new Date(event.end))   : '11:00',
        changeTime:             false,
        capacityUnitsRequested: event?.capacityUsed ?? 1,
        changeCapacity:         false,
        customerFullName:       event?.customerFullName ?? '',
        customerPhone:          event?.customerPhone    ?? '',
        customerEmail:          event?.customerEmail    ?? '',
        note:                   event?.note             ?? '',
        isPaid:                 event?.isPaid           ?? 0,
        statusId:               event?.statusId         ?? '',
        manualPriceOverride:    event?.manualPriceOverride ?? null,
        changeManualPrice:      false,
    }), [event]);

    const [form, setForm] = useState<EditFormState>(defaultForm);

    const reset = useCallback(() => {
        setView('choose');
        setDeleteMode('thisAndFuture');
        setWorking(false);
        setError(null);
        setEditResult(null);
        setDeleteResult(null);
        setShowEditErrors(false);
        setForm(defaultForm);
    }, [defaultForm]);

    const handleClose = useCallback(() => { reset(); onClose(); }, [reset, onClose]);

    // FIX #1 – isPast is now INFORMATIONAL ONLY. It no longer blocks any action.
    // Past recurring occurrences can be edited (metadata) or deleted just like future ones.
    const isPast = event ? new Date(event.start) < new Date() : false;
    const isMobile = window.innerWidth <= 768;

    // FIX #1 – removed `isPast` guard. Past events can be detached and edited.
    // The backend's EditRecurringSeries applies metadata-only changes to past slots,
    // and DetachFromSeries no longer rejects past bookings.
    const handleThisOnly = useCallback(() => {
        if (!event) return;
        handleClose();
        onThisOnly(event);
    }, [event, handleClose, onThisOnly]);

    // ── Edit all in series ────────────────────────────────────────────────────

    // Validate the edit form fields that mirror the create-series validation.
    const editFormErrors = useMemo(() => {
        const errs: { customerPhone?: string; customerEmail?: string; slotTime?: string } = {};
        if (!form.customerPhone?.trim())
            errs.customerPhone = t('recurring.validation.phoneRequired');
        if (!form.customerEmail?.trim())
            errs.customerEmail = t('recurring.validation.emailRequired');
        if (form.changeTime && form.newSlotStartTime >= form.newSlotEndTime)
            errs.slotTime = t('recurring.validation.slotEndAfterStart');
        return errs;
    }, [form.customerPhone, form.customerEmail, form.changeTime, form.newSlotStartTime, form.newSlotEndTime, t]);

    const hasEditErrors = Object.keys(editFormErrors).length > 0;

    const handleEditAll = useCallback(async () => {
        if (!event?.recurringSeriesId) return;

        // Show field errors and abort if validation fails
        if (hasEditErrors) {
            setShowEditErrors(true);
            return;
        }
        setShowEditErrors(false);
        setWorking(true); setError(null);

        const dto: UpdateRecurringSeriesDTO = {
            capacityUnitsRequested: form.changeCapacity ? form.capacityUnitsRequested : 0,
            newSlotStart: form.changeTime ? localHHmmToIso(new Date(event!.start), form.newSlotStartTime) : undefined,
            newSlotEnd:   form.changeTime ? localHHmmToIso(new Date(event!.start), form.newSlotEndTime)   : undefined,
            customerFullName:       form.customerFullName || null,
            customerPhone:          form.customerPhone    || null,
            customerEmail:          form.customerEmail    || null,
            note:                   form.note             || null,
            isPaid:                 form.isPaid,
            statusId:               form.statusId ? form.statusId : undefined,
            // BUG #1 FIX: only send override when user explicitly changed it
            manualPriceOverride:    form.changeManualPrice ? form.manualPriceOverride : undefined,
        };

        const result = await calendarStore.editRecurringSeries(event.recurringSeriesId, dto);
        setWorking(false);
        setEditResult(result);
        setView('editAllResult');
    }, [event, form, calendarStore]);

    const handleDeleteConfirmed = useCallback(async () => {
        if (!event?.recurringSeriesId) return;
        setWorking(true); setError(null);

        let result: DeleteRecurringResult;

        if (deleteMode === 'thisOnly') {
            const ok = await calendarStore.detachEventFromSeries(event.id);
            if (!ok) { setError(t('recurring.action.detachError')); setWorking(false); return; }
            try {
                await calendarStore.deleteEvent(event.id);
                result = { type: 'success', data: { futureSlotsDeleted: 1, pastSlotsProtected: 0 } };
            } catch {
                setError(t('recurring.action.deleteSeriesError')); setWorking(false); return;
            }
        } else if (deleteMode === 'thisAndFuture') {
            result = await calendarStore.deleteRecurringFromDate(
                event.recurringSeriesId,
                new Date(event.start),
            );
        } else {
            result = await calendarStore.deleteRecurringSeries(event.recurringSeriesId);
        }

        setWorking(false);
        if (result.type === 'error') { setError(t('recurring.action.deleteSeriesError')); return; }
        setDeleteResult(result);
        setView('deleteResult');
        onSeriesDeleted(result);
    }, [event, deleteMode, calendarStore, t, onSeriesDeleted]);

    if (!event) return null;

    // FIX #1 – removed the early-return "past event" modal that blocked all interaction.
    // Past events now open the full dialog. We show a contextual info banner instead
    // so the user understands that time/capacity changes won't apply to past slots.

    const isEdit = intent === 'edit';

    const capacityPct = totalCapacity > 0
        ? Math.round((form.capacityUnitsRequested / totalCapacity) * 100)
        : 0;
    const pctColor = capacityPct > 75 ? 'red' : capacityPct > 40 ? 'orange' : 'green';

    const effectivePrice      = event.manualPriceOverride ?? event.priceFinal;
    const hasExistingOverride = event.manualPriceOverride != null;

    const isDeleteView = view === 'deleteConfirm' || view === 'deleteResult';

    return (
        <Modal
            open={open}
            onClose={handleClose}
            size={view === 'editAllForm' ? 'small' : 'tiny'}
            closeOnDimmerClick={!working}
            style={{ borderRadius: '10px', overflow: 'hidden' }}
        >
            {/* ── Header ── */}
            <Modal.Header style={{
                background:   isDeleteView ? '#fff3f3' : '#f0f7ff',
                borderBottom: `2px solid ${isDeleteView ? '#f5c6cb' : '#b8d4f0'}`,
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: isMobile ? '0.8rem 1rem' : '0.9rem 1.25rem',
            }}>
                <Icon
                    name={isDeleteView ? 'trash alternate outline' : 'edit outline'}
                    style={{ color: isDeleteView ? '#dc3545' : '#1a73e8' }}
                />
                {t(isDeleteView ? 'recurring.action.deleteTitle' : 'recurring.action.editTitle')}
            </Modal.Header>

            <Modal.Content style={{
                maxHeight: '72vh', overflowY: 'auto',
                padding: isMobile ? '1rem' : '1.1rem 1.25rem',
            }}>
                {error && (
                    <Message negative size="tiny" icon style={{ marginBottom: '0.75rem' }}>
                        <Icon name="exclamation circle" />
                        <Message.Content>{error}</Message.Content>
                    </Message>
                )}
                {working && (
                    <div style={{ textAlign: 'center', padding: '0.75rem' }}>
                        <Loader active inline="centered" size="small" />
                    </div>
                )}

                {/* FIX #1 – Past event info banner (informational, not blocking) */}
                {isPast && view === 'choose' && (
                    <Message info size="tiny" icon style={{ marginBottom: '0.75rem' }}>
                        <Icon name="history" />
                        <Message.Content>
                            <Message.Header>{t('recurring.action.pastEventHeader')}</Message.Header>
                            <p>{t('recurring.action.pastEventBody')}</p>
                        </Message.Content>
                    </Message>
                )}

                {/* ══ CHOOSE ══ */}
                {view === 'choose' && (
                    <p style={{ fontSize: '1rem', color: '#555', margin: 0 }}>
                        {t('recurring.action.editQuestion')}
                    </p>
                )}

                {/* ══ EDIT ALL FORM ══ */}
                {view === 'editAllForm' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                        <Message info size="tiny" icon>
                            <Icon name="info circle" />
                            <Message.Content>{t('recurring.action.editAllHint')}</Message.Content>
                        </Message>

                        {/* Validation summary – shown only after first save attempt */}
                        {showEditErrors && hasEditErrors && (
                            <Message negative size="small" icon>
                                <Icon name="exclamation triangle" />
                                <Message.Content>
                                    <Message.Header>{t('recurring.validation.summaryTitle')}</Message.Header>
                                    <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.2rem' }}>
                                        {Object.values(editFormErrors).map((msg, i) => (
                                            <li key={i}>{msg}</li>
                                        ))}
                                    </ul>
                                </Message.Content>
                            </Message>
                        )}

                        {/* Time toggle */}
                        <Segment secondary style={{ padding: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: form.changeTime ? '0.75rem' : 0 }}>
                                <input type="checkbox" id="changeTime" checked={form.changeTime}
                                       onChange={e => setForm(p => ({ ...p, changeTime: e.target.checked }))} />
                                <label htmlFor="changeTime" style={{ ...labelStyle, marginBottom: 0 }}>
                                    {t('recurring.action.changeTimeOfDay')}
                                </label>
                            </div>
                            {form.changeTime && (
                                <Grid columns={2} stackable>
                                    <Grid.Column>
                                        <label style={labelStyle}>{t('recurring.form.slotStart')}</label>
                                        <input type="time" value={form.newSlotStartTime} style={inputStyle}
                                               onChange={e => setForm(p => ({ ...p, newSlotStartTime: e.target.value }))} />
                                    </Grid.Column>
                                    <Grid.Column>
                                        <label style={labelStyle}>{t('recurring.form.slotEnd')}</label>
                                        <input type="time" value={form.newSlotEndTime} style={inputStyle}
                                               onChange={e => setForm(p => ({ ...p, newSlotEndTime: e.target.value }))} />
                                    </Grid.Column>
                                </Grid>
                            )}
                            {showEditErrors && editFormErrors.slotTime && (
                                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.4rem' }}>
                                    <Icon name="exclamation circle" />{editFormErrors.slotTime}
                                </div>
                            )}
                        </Segment>

                        {/* Capacity toggle */}
                        <Segment secondary style={{ padding: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: form.changeCapacity ? '0.75rem' : 0 }}>
                                <input type="checkbox" id="changeCapacity" checked={form.changeCapacity}
                                       onChange={e => setForm(p => ({ ...p, changeCapacity: e.target.checked }))} />
                                <label htmlFor="changeCapacity" style={{ ...labelStyle, marginBottom: 0 }}>
                                    {t('recurring.action.changeCapacity')}
                                </label>
                            </div>
                            {form.changeCapacity && totalCapacity > 0 && (
                                <div>
                                    <input
                                        type="range" min={1} max={totalCapacity}
                                        value={form.capacityUnitsRequested}
                                        style={{ width: '100%', marginBottom: '0.5rem' }}
                                        onChange={e => setForm(p => ({ ...p, capacityUnitsRequested: Number(e.target.value) }))}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                                        <Label color={pctColor} size="small">
                                            {capacityPct}% &mdash; {form.capacityUnitsRequested}/{totalCapacity} {t('capacity.units')}
                                        </Label>
                                        <Progress
                                            value={form.capacityUnitsRequested} total={totalCapacity}
                                            color={pctColor} size="tiny"
                                            style={{ flex: 1, margin: 0 }}
                                        />
                                    </div>
                                </div>
                            )}
                            {form.changeCapacity && totalCapacity === 0 && (
                                <input type="number" min={1} value={form.capacityUnitsRequested}
                                       style={{ ...inputStyle, marginTop: '0.5rem' }}
                                       onChange={e => setForm(p => ({ ...p, capacityUnitsRequested: Number(e.target.value) }))} />
                            )}
                        </Segment>

                        {/* BUG #1 FIX: Manual price override toggle */}
                        <Segment secondary style={{ padding: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: form.changeManualPrice ? '0.75rem' : 0 }}>
                                <input type="checkbox" id="changeManualPrice" checked={form.changeManualPrice}
                                       onChange={e => setForm(p => ({
                                           ...p,
                                           changeManualPrice: e.target.checked,
                                           manualPriceOverride: e.target.checked ? (p.manualPriceOverride ?? null) : null,
                                       }))} />
                                <label htmlFor="changeManualPrice" style={{ ...labelStyle, marginBottom: 0 }}>
                                    {t('recurring.action.changeManualPrice')}
                                </label>
                            </div>

                            {effectivePrice != null && (
                                <div style={{ fontSize: '0.82rem', color: '#666', marginBottom: form.changeManualPrice ? '0.6rem' : 0 }}>
                                    {t('recurring.action.currentPrice')}:{' '}
                                    <strong style={{ color: hasExistingOverride ? '#e67e22' : '#2c3e50' }}>
                                        ${effectivePrice.toFixed(2)}
                                    </strong>
                                    {hasExistingOverride && (
                                        <span style={{ marginLeft: '0.4rem', color: '#e67e22', fontSize: '0.78rem' }}>
                                            ({t('calendar.overrideIndicator')})
                                        </span>
                                    )}
                                </div>
                            )}

                            {form.changeManualPrice && (
                                <div>
                                    <label style={labelStyle}>{t('pricing.manualOverride')}</label>
                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                        <input
                                            type="number"
                                            min={0}
                                            step="0.01"
                                            placeholder={t('pricing.enterOverridePrice')}
                                            value={form.manualPriceOverride ?? ''}
                                            style={{ ...inputStyle, flex: 1 }}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setForm(p => ({
                                                    ...p,
                                                    manualPriceOverride: val === '' ? null : parseFloat(val),
                                                }));
                                            }}
                                        />
                                        {form.manualPriceOverride != null && (
                                            <Button
                                                size="tiny" icon="times" color="grey"
                                                title={t('pricing.clearOverride')}
                                                onClick={() => setForm(p => ({ ...p, manualPriceOverride: null }))}
                                            />
                                        )}
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.3rem' }}>
                                        {t('recurring.action.manualPriceHint')}
                                    </div>
                                </div>
                            )}
                        </Segment>

                        {/* Customer details */}
                        <Grid stackable columns={isMobile ? 1 : 3}>
                            {[
                                { key: 'customerFullName', type: 'text',  label: t('calendars.modal.customerFullName'), errKey: undefined },
                                { key: 'customerPhone',    type: 'tel',   label: t('calendars.modal.customerPhone'),    errKey: 'customerPhone' as const },
                                { key: 'customerEmail',    type: 'email', label: t('calendars.modal.customerEmail'),    errKey: 'customerEmail' as const },
                            ].map(({ key, type, label, errKey }) => {
                                const fieldErr = errKey && showEditErrors ? editFormErrors[errKey] : undefined;
                                return (
                                    <Grid.Column key={key}>
                                        <Form.Field error={!!fieldErr}>
                                            <label style={labelStyle}>{label}{errKey ? ' *' : ''}</label>
                                            <input
                                                type={type}
                                                value={(form as any)[key]}
                                                style={{ ...inputStyle, borderColor: fieldErr ? '#dc3545' : '#ced4da' }}
                                                onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                                            />
                                            {fieldErr && (
                                                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                                    <Icon name="exclamation circle" />{fieldErr}
                                                </div>
                                            )}
                                        </Form.Field>
                                    </Grid.Column>
                                );
                            })}
                        </Grid>

                        {/* Note */}
                        <Form.Field>
                            <label style={labelStyle}>{t('calendars.modal.note')}</label>
                            <textarea value={form.note} rows={isMobile ? 3 : 2}
                                      style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                                      onChange={e => setForm(p => ({ ...p, note: e.target.value }))} />
                        </Form.Field>

                        {/* Status */}
                        <Form.Field>
                            <label style={labelStyle}>{t('calendars.modal.status')}</label>
                            <select value={form.statusId}
                                    style={{ ...inputStyle, appearance: 'none' }}
                                    onChange={e => setForm(p => ({ ...p, statusId: e.target.value }))}>
                                <option value="">{t('calendars.modal.selectStatus')}</option>
                                {statusOptions.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </Form.Field>

                        {/* Payment */}
                        <Form.Field>
                            <label style={labelStyle}>{t('calendars.modal.paymentStatus')}</label>
                            <PaymentStatusSelector value={form.isPaid}
                                                   onChange={v => setForm(p => ({ ...p, isPaid: v }))} fluid />
                        </Form.Field>
                    </div>
                )}

                {/* ══ EDIT ALL RESULT ══ */}
                {view === 'editAllResult' && editResult && (
                    <EditResultPanel result={editResult} t={t} />
                )}

                {/* ══ DELETE CONFIRM ══ */}
                {view === 'deleteConfirm' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                        <p style={{ margin: '0 0 0.4rem', fontSize: '0.95rem', color: '#555' }}>
                            {t('recurring.action.confirmDeleteChoose')}
                        </p>

                        {([
                            {
                                mode:  'thisOnly'      as DeleteMode,
                                label: t('recurring.action.deleteThisOnly'),
                                desc:  t('recurring.action.deleteThisOnlyDesc'),
                                icon:  'calendar times outline' as const,
                            },
                            {
                                mode:  'thisAndFuture' as DeleteMode,
                                label: t('recurring.action.deleteThisAndFuture'),
                                desc:  t('recurring.action.deleteThisAndFutureDesc'),
                                icon:  'forward' as const,
                            },
                            {
                                mode:  'allFuture'     as DeleteMode,
                                label: t('recurring.action.deleteEntireSeries'),
                                desc:  t('recurring.action.deleteEntireSeriesDesc'),
                                icon:  'trash alternate outline' as const,
                            },
                        ] as const).map(opt => (
                            <div
                                key={opt.mode}
                                onClick={() => setDeleteMode(opt.mode)}
                                style={{
                                    cursor: 'pointer', padding: '0.7rem 0.9rem',
                                    border: `2px solid ${deleteMode === opt.mode ? '#dc3545' : '#dee2e6'}`,
                                    borderRadius: '8px',
                                    background: deleteMode === opt.mode ? '#fff5f5' : '#fff',
                                    display: 'flex', alignItems: 'flex-start', gap: '0.7rem',
                                    transition: 'border-color 0.15s, background 0.15s',
                                }}
                            >
                                <input type="radio" readOnly checked={deleteMode === opt.mode}
                                       style={{ marginTop: '3px', flexShrink: 0 }} />
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.93rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                        <Icon name={opt.icon} style={{ margin: 0 }} />
                                        {opt.label}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#777', marginTop: '0.2rem', lineHeight: 1.35 }}>
                                        {opt.desc}
                                    </div>
                                </div>
                            </div>
                        ))}

                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#888' }}>
                            <Icon name="lock" color="blue" size="small" />
                            {t('recurring.action.confirmDeleteSeriesPastProtected')}
                        </p>
                    </div>
                )}

                {/* ══ DELETE RESULT ══ */}
                {view === 'deleteResult' && deleteResult?.type === 'success' && (
                    <Message positive>
                        <Message.Header>
                            {t('recurring.action.deleteSeriesDoneHeader',
                               { count: deleteResult.data.futureSlotsDeleted })}
                        </Message.Header>
                        {deleteResult.data.pastSlotsProtected > 0 && (
                            <p style={{ marginTop: '0.4rem' }}>
                                <Label color="blue" size="small">
                                    <Icon name="lock" />
                                    {t('recurring.action.pastSlotsProtected',
                                       { count: deleteResult.data.pastSlotsProtected })}
                                </Label>
                            </p>
                        )}
                    </Message>
                )}
            </Modal.Content>

            {/* ── Footer ── */}
            <Modal.Actions style={{
                background: '#f8f9fa',
                padding: isMobile ? '0.75rem' : '0.75rem 1.1rem',
                display: 'flex', flexDirection: 'column', gap: '0.45rem',
            }}>
                {/* ══ CHOOSE view ══ */}
                {view === 'choose' && (
                    <>
                        <Button fluid basic icon="times" onClick={handleClose} disabled={working}
                                content={t('calendars.modal.cancel')} />
                        <Divider style={{ margin: '0.1rem 0' }} />

                        {/* Edit this occurrence only */}
                        <Button fluid color="blue"
                                onClick={handleThisOnly} disabled={working}
                                icon="edit"
                                content={t('recurring.action.editThisOnly')} />

                        {/* Edit all in series */}
                        <Button fluid color="teal" basic
                                onClick={() => { setForm(defaultForm); setView('editAllForm'); }}
                                disabled={working} icon="repeat"
                                content={t('recurring.action.editAllInSeries')} />

                        {/* ── BUG #2 FIX: Delete button always visible ── */}
                        <Divider horizontal style={{ margin: '0.2rem 0' }}>
                            <span style={{ color: '#aaa', fontSize: '0.75rem' }}>
                                {t('recurring.action.orDeleteSection')}
                            </span>
                        </Divider>
                        <Button fluid color="red" basic
                                onClick={() => setView('deleteConfirm')}
                                disabled={working} icon="trash alternate"
                                content={t('recurring.action.deleteOptions')} />
                    </>
                )}

                {/* EDIT ALL FORM view */}
                {view === 'editAllForm' && (
                    <>
                        <Button fluid positive loading={working} disabled={working}
                                onClick={handleEditAll} icon="save" labelPosition="right"
                                content={t('recurring.action.saveAllInSeries')} />
                        <Button fluid basic onClick={() => setView('choose')} disabled={working}
                                icon="arrow left" content={t('common.back')} />
                    </>
                )}

                {/* EDIT ALL RESULT view */}
                {view === 'editAllResult' && (
                    <Button fluid positive onClick={handleClose} icon="check"
                            content={t('common.close')} />
                )}

                {/* DELETE CONFIRM view */}
                {view === 'deleteConfirm' && (
                    <>
                        <Button fluid color="red" loading={working} disabled={working}
                                onClick={handleDeleteConfirmed} icon="trash alternate"
                                content={t('recurring.action.confirmDeleteSeriesYes')} />
                        <Button fluid basic onClick={() => setView('choose')} disabled={working}
                                content={t('common.back')} />
                    </>
                )}

                {/* DELETE RESULT view */}
                {view === 'deleteResult' && (
                    <Button fluid positive onClick={handleClose} icon="check"
                            content={t('common.close')} />
                )}
            </Modal.Actions>
        </Modal>
    );
});

export default RecurringActionDialog;

// ─── Edit result sub-panel ────────────────────────────────────────────────────

interface EditResultPanelProps { result: EditRecurringResult; t: TFunc; }

const EditResultPanel: React.FC<EditResultPanelProps> = ({ result, t }) => {
    if (result.type === 'error') {
        return (
            <Message negative icon>
                <Icon name="times circle" />
                <Message.Content>
                    <Message.Header>{t('recurring.result.errorTitle')}</Message.Header>
                    <p>{result.message}</p>
                </Message.Content>
            </Message>
        );
    }

    const data      = result.data;
    const allFailed = result.type === 'nothing';
    const partial   = result.type === 'partial';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Segment color={allFailed ? 'red' : partial ? 'orange' : 'green'} textAlign="center">
                <Icon
                    name={allFailed ? 'times circle' : partial ? 'exclamation triangle' : 'check circle'}
                    size="large" color={allFailed ? 'red' : partial ? 'orange' : 'green'}
                />
                <Header as="h4" style={{ marginTop: '0.4rem' }}>
                    {allFailed ? t('recurring.editResult.nothingTitle')
                        : partial ? t('recurring.editResult.partialTitle')
                        : t('recurring.editResult.successTitle')}
                </Header>
                {!allFailed && (
                    <>
                        <p style={{ color: '#555', margin: '0.2rem 0' }}>
                            {t('recurring.editResult.updatedCount', { count: data.slotsUpdated })}
                        </p>
                        {partial && (
                            <Progress
                                value={data.slotsUpdated}
                                total={data.slotsUpdated + data.slotsSkipped}
                                color="orange" size="small"
                                style={{ margin: '0.5rem auto', maxWidth: '240px' }}
                                label={`${data.slotsUpdated} / ${data.slotsUpdated + data.slotsSkipped}`}
                            />
                        )}
                    </>
                )}
                {allFailed && (
                    <p style={{ color: '#888', marginTop: '0.3rem', fontSize: '0.9rem' }}>
                        {t('recurring.editResult.nothingBody')}
                    </p>
                )}
            </Segment>

            {data.skippedSlots?.length > 0 && (
                <Message warning size="small">
                    <Message.Header>
                        {t('recurring.editResult.skippedTitle', { count: data.slotsSkipped })}
                    </Message.Header>
                    <p>{t('recurring.result.skippedBody')}</p>
                    <div style={{
                        marginTop: '0.5rem', maxHeight: '180px', overflowY: 'auto',
                        border: '1px solid #f0ad4e', borderRadius: '4px',
                    }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <thead style={{ background: '#fff9e6' }}>
                                <tr>
                                    {[t('recurring.conflict.date'), t('recurring.conflict.used'), t('recurring.conflict.available')].map(h => (
                                        <th key={h} style={{ padding: '0.4rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #f0ad4e' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.skippedSlots.map((s: RecurringSkippedSlotDTO, i: number) => (
                                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fffdf0' }}>
                                        <td style={{ padding: '0.35rem 0.6rem' }}>
                                            {new Date(s.occurrenceDate).toLocaleDateString(undefined, {
                                                weekday: 'short', day: 'numeric', month: 'short',
                                            })}
                                        </td>
                                        <td style={{ padding: '0.35rem 0.6rem' }}>{s.usedCapacity}/{s.totalCapacity}</td>
                                        <td style={{ padding: '0.35rem 0.6rem' }}>
                                            <Label size="tiny" color={s.availableCapacity === 0 ? 'red' : 'orange'}>
                                                {s.availableCapacity}
                                            </Label>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Message>
            )}
        </div>
    );
};